
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Outlet } from 'react-router-dom';
import { AdminRoutes } from './AdminRoutes';
import { MainLayout } from '../components/layout/MainLayout';
import { RequireRole } from '../components/shared/RequireRole';
import { BrgDetailPage } from '../features/shared/BrgDetailPage';
import { BudgetsPage } from '../features/shared/BudgetsPage';
import { BudgetDashboard } from '../features/shared/BudgetDashboard';
import { BudgetRequestPage } from '../features/shared/BudgetRequestPage';
import { MembershipDataPage } from '../features/shared/MembershipDataPage';
import { ResourceHubPage } from '../features/shared/ResourceHubPage';
import EngagementMetricsPage from '../features/shared/engagementMetrics/EngagementMetricsPage';
import CalendarPage from '../features/calendar/CalendarPage';
import { ProjectTrackingPage } from '../features/ProjectTracking/ProjectTrackingPage';
import { Layout } from '../components/Layout';
import { LoginPage } from '../features/login/LoginPage';
import { LoginCallbackPage } from '../features/login/LoginCallbackPage';
import { LogoutPage } from '../features/login/LogoutPage';

function UnauthorizedPage() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px',
        textAlign: 'center',
        padding: '24px',
      }}
    >
      <div style={{ fontSize: '4rem', lineHeight: 1 }}>&#128683;</div>
      <h1 style={{ fontSize: '1.6rem', color: '#d32f2f', margin: 0 }}>Access Denied</h1>
      <p style={{ color: '#666', maxWidth: '400px' }}>
        You do not have permission to access this page. Please contact your administrator if you
        believe this is an error.
      </p>
      <a
        href="/"
        style={{
          color: '#0078d4',
          textDecoration: 'underline',
          fontSize: '0.95rem',
        }}
      >
        Return to Home
      </a>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px',
        textAlign: 'center',
        padding: '24px',
      }}
    >
      <div style={{ fontSize: '4rem', fontWeight: 700, color: '#ccc' }}>404</div>
      <h1 style={{ fontSize: '1.4rem', color: '#444', margin: 0 }}>Page Not Found</h1>
      <p style={{ color: '#888' }}>The page you are looking for does not exist.</p>
      <a href="/" style={{ color: '#0078d4', textDecoration: 'underline' }}>
        Go to Home
      </a>
    </div>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/callback" element={<LoginCallbackPage />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route element={<Layout><Outlet /></Layout>}>
          <Route path="/" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/*" element={<AdminRoutes />} />
          <Route
            path="/brgs/:id"
            element={
              <RequireRole role="BRG.Member">
                <BrgDetailPage />
              </RequireRole>
            }
          />
          {/* Calendar route */}
          <Route
            path="/calendar"
            element={
              <RequireRole role="BRG-Lead">
                <MainLayout>
                  <CalendarPage />
                </MainLayout>
              </RequireRole>
            }
          />
          {/* Budgets route */}
          <Route
            path="/budgets"
            element={
              <RequireRole role="BRG-Lead">
                <MainLayout>
                  <BudgetsPage />
                </MainLayout>
              </RequireRole>
            }
          />
          {/* Budget Request Form route */}
          <Route
            path="/budgets/request"
            element={
              <RequireRole role="BRG.Member">
                <MainLayout>
                  <BudgetRequestPage />
                </MainLayout>
              </RequireRole>
            }
          />
          {/* Budget Dashboard route */}
          <Route
            path="/budget-dashboard"
            element={
              <RequireRole role="BRG-Lead">
                <MainLayout>
                  <BudgetDashboard />
                </MainLayout>
              </RequireRole>
            }
          />
          {/* Membership Data route */}
          <Route
            path="/membership"
            element={
              <RequireRole role="BRG-Lead">
                <MainLayout>
                  <MembershipDataPage />
                </MainLayout>
              </RequireRole>
            }
          />
          {/* Resource Hub route — Admin + Lead view; Admin-only mutations enforced inside the page */}
          <Route
            path="/resources"
            element={
              <RequireRole role="BRG-Lead">
                <MainLayout>
                  <ResourceHubPage />
                </MainLayout>
              </RequireRole>
            }
          />
          {/* Project Tracking route */}
          <Route
            path="/projects"
            element={
              <RequireRole role="BRG-Lead">
                <MainLayout>
                  <ProjectTrackingPage />
                </MainLayout>
              </RequireRole>
            }
          />
          {/* Engagement Metrics route — story 509754 */}
          <Route
            path="/engagement"
            element={
              <RequireRole role="BRG-Lead">
                <MainLayout>
                  <EngagementMetricsPage />
                </MainLayout>
              </RequireRole>
            }
          />
          {/* Stub routes for future pages */}
          <Route
            path="/brgs/:id/calendar"
            element={
              <RequireRole role="BRG.Member">
                <MainLayout>
                  <CalendarPage />
                </MainLayout>
              </RequireRole>
            }
          />
          <Route
            path="/brgs/:id/engagement"
            element={
              <RequireRole role="BRG.Member">
                <StubPage title="Engagement" />
              </RequireRole>
            }
          />
          <Route
            path="/brgs/:id/budget"
            element={
              <RequireRole role="BRG.Member">
                <StubPage title="Budget" />
              </RequireRole>
            }
          />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

function StubPage({ title }: { title: string }) {
  const navigate = useNavigate();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '12px',
        color: '#888',
      }}
    >
      <h2 style={{ margin: 0 }}>{title}</h2>
      <p>This page is coming soon.</p>
      <button
        onClick={() => void navigate(-1)}
        style={{
          background: 'none',
          border: 'none',
          color: '#0078d4',
          textDecoration: 'underline',
          cursor: 'pointer',
          fontSize: '0.95rem',
        }}
      >
        Go Back
      </button>
    </div>
  );
}
